import { createObjectUrlFromBinaryResource } from '../../../../helpers/viewerBinaryCache';
import { prioritizeThumbnailUrlCandidates } from '../thumbnailHelpers';

function createThumbnailReleasedError() {
    const error = new Error('Thumbnail acquisition was released.');
    error.name = 'AbortError';
    return error;
}

function createDecodedThumbnailObjectUrl(resource) {
    const objectUrl = createObjectUrlFromBinaryResource(resource, 'image/jpeg');

    if (typeof Image === 'undefined') {
        return Promise.resolve(objectUrl);
    }

    return new Promise((resolve, reject) => {
        const image = new Image();

        image.onload = () => {
            image.onload = null;
            image.onerror = null;
            resolve(objectUrl);
        };
        image.onerror = (error) => {
            image.onload = null;
            image.onerror = null;
            URL.revokeObjectURL(objectUrl);
            reject(error || new Error('Thumbnail image failed to decode.'));
        };
        image.src = objectUrl;
    });
}

export function acquireThumbnailFromCacheCandidates(thumbnailUrlCandidates, {
    consumerId,
    preferredUrl,
    viewerBinaryCache,
} = {}) {
    if (!viewerBinaryCache) {
        throw new Error('acquireThumbnailFromCacheCandidates requires a viewer binary cache instance.');
    }

    const prioritizedThumbnailUrlCandidates = prioritizeThumbnailUrlCandidates(
        thumbnailUrlCandidates,
        preferredUrl,
    );
    let activeHandle = null;
    let released = false;

    const releaseActiveHandle = () => {
        activeHandle?.release();
        activeHandle = null;
    };

    const release = () => {
        released = true;
        releaseActiveHandle();
    };

    const tryNextCandidate = (candidateIndex) => {
        if (released) {
            return Promise.reject(createThumbnailReleasedError());
        }

        if (candidateIndex >= prioritizedThumbnailUrlCandidates.length) {
            return Promise.reject(new Error('No thumbnail candidates succeeded.'));
        }

        activeHandle = viewerBinaryCache.acquire({
            consumerId,
            type: 'thumbnail',
            url: prioritizedThumbnailUrlCandidates[candidateIndex],
        });

        return activeHandle.promise
            .then((resource) => createDecodedThumbnailObjectUrl(resource).then((objectUrl) => {
                if (released) {
                    URL.revokeObjectURL(objectUrl);
                    throw createThumbnailReleasedError();
                }

                releaseActiveHandle();

                return {
                    objectUrl,
                    resource,
                    resolvedUrl: prioritizedThumbnailUrlCandidates[candidateIndex],
                };
            }))
            .catch((error) => {
                if (viewerBinaryCache.isAbortError(error)) {
                    throw error;
                }

                releaseActiveHandle();
                viewerBinaryCache.invalidate({
                    type: 'thumbnail',
                    url: prioritizedThumbnailUrlCandidates[candidateIndex],
                });

                return tryNextCandidate(candidateIndex + 1);
            });
    };

    return {
        promise: tryNextCandidate(0),
        release,
    };
}
